import { validate as validateUuid } from 'uuid';

export const ScanTypes = Object.freeze({
  TRAVELER: 'traveler',
  LOT: 'lot',
});

/**
 * Parse a scanned/typed code into `{ type, id, error }`.
 *
 * Travelers are bare UUIDs or `/travelers/<uuid>` URLs. Lots are
 * `/lots/<docId>` URLs — lot labels encode the full admin URL, and the
 * origin is ignored so labels printed against dev/staging still scan.
 * The two shapes are disjoint, so type detection is unambiguous.
 */
export default function parseScanCode(value) {
  const rawValue = typeof value === 'string' ? value.trim() : '';
  if (!rawValue) {
    return { type: null, id: null, error: 'Empty QR code.' };
  }

  const isValidUuid = (id) => Boolean(id) && validateUuid(id);
  if (isValidUuid(rawValue)) {
    return { type: ScanTypes.TRAVELER, id: rawValue, error: null };
  }

  try {
    const url = new URL(rawValue);
    const path = url.pathname.split('/').filter(Boolean);
    // Only the canonical entity URLs that labels encode match. Deeper or
    // prefixed paths (/lots/<id>/edit, /foo/travelers/<id>) are not
    // scannable entities and fall through to the error.
    if (path.length === 2 && path[0] === 'travelers' && isValidUuid(path[1])) {
      return { type: ScanTypes.TRAVELER, id: path[1], error: null };
    }
    if (path.length === 2 && path[0] === 'lots' && path[1]) {
      return { type: ScanTypes.LOT, id: path[1], error: null };
    }
  } catch {
    // Fall through to the shared error below.
  }

  return { type: null, id: null, error: `Invalid QR code: ${rawValue}` };
}
